import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Button,
    Typography,
    useTheme,
    useMediaQuery,
    Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import useBusinessPermissions from "src/hooks/useBusinessPermissions";

const PermissionsDialog = ({ open, onClose, permissions, onSave, setValue, watch_location, locationIndex, systemRole }) => {

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const { isOwnBusiness } = useBusinessPermissions();

    const handleChange = (permKey) => {
        // Prevent changing manageTeamMembers and manageWallet if not owner
        if (!isOwnBusiness && (permKey === "manageTeamMembers" || permKey === "manageWallet")) {
            return;
        }

        const isCurrentlyChecked = permissions?.[permKey] === true;
        const updatedPermissions = { ...permissions };

        // Handle "no permissions" (viewNothing)
        if (permKey === "viewNothing") {
            if (!isCurrentlyChecked) {
                // When enabling "no permissions", disable all other permissions
                updatedPermissions.viewNothing = true;
                updatedPermissions.viewOnly = false;
                // Set all other permissions to false
                Object.keys(updatedPermissions).forEach(key => {
                    if (key !== "viewNothing") {
                        updatedPermissions[key] = false;
                    }
                });
            } else {
                // When unchecking "no permissions", just toggle it
                updatedPermissions.viewNothing = false;
            }
        }
        // Handle "view only" (viewOnly)
        else if (permKey === "viewOnly") {
            updatedPermissions.viewOnly = !isCurrentlyChecked;
            // If enabling viewOnly, disable viewNothing and all other permissions
            if (!isCurrentlyChecked) {
                updatedPermissions.viewNothing = false;
                // Set all other permissions to false (except viewOnly and viewNothing)
                Object.keys(updatedPermissions).forEach(key => {
                    if (key !== "viewOnly" && key !== "viewNothing") {
                        updatedPermissions[key] = false;
                    }
                });
            }
        }
        // Handle other permissions (any permission except viewNothing and viewOnly)
        else {
            updatedPermissions[permKey] = !isCurrentlyChecked;
            // If enabling any other permission, disable viewOnly and viewNothing
            if (!isCurrentlyChecked) {
                updatedPermissions.viewOnly = false;
                updatedPermissions.viewNothing = false;
            }
        }

        // Update the specific location permissions in the form
        const updatedLocations = [...watch_location];
        if (locationIndex !== null && updatedLocations[locationIndex]) {
            updatedLocations[locationIndex] = {
                ...updatedLocations[locationIndex],
                permissions: updatedPermissions,
            };
            setValue("location", updatedLocations);
        }
    };

    // Get available permissions based on the selected system role
    const getAvailablePermissions = () => {
        if (!systemRole || systemRole.roleName === 'default') {
            return [];
        }
        
        const rolePermissions = systemRole.permissions || {};
        const availablePermissions = [];
        
        // Only show permissions that are true in the system role
        if (rolePermissions.viewOnly) availablePermissions.push({ label: "view only", value: "viewOnly" });
        if (rolePermissions.viewNothing) availablePermissions.push({ 
            label: "no permissions", 
            value: "viewNothing",
            subtitle: "(recommended for non-managers)" 
        });
        if (rolePermissions.editLocationProfile) availablePermissions.push({ label: "manage location profile", value: "editLocationProfile" });
        if (rolePermissions.manageTeamMembers) availablePermissions.push({ label: "manage team members", value: "manageTeamMembers" });
        if (rolePermissions.manageChats) availablePermissions.push({ label: "manage chats", value: "manageChats" });
        if (rolePermissions.manageMenu) availablePermissions.push({ label: "manage menu", value: "manageMenu" });
        if (rolePermissions.postPhotosVideos) availablePermissions.push({ label: "manage posts/photos/videos", value: "postPhotosVideos" });
        if (rolePermissions.createEvents) availablePermissions.push({ label: "manage events", value: "createEvents" });
        if (rolePermissions.createOrders) availablePermissions.push({ label: "create orders", value: "createOrders" });
        if (rolePermissions.manageWallet) availablePermissions.push({ label: "manage wallet", value: "manageWallet" });
        
        return availablePermissions;
    };

    const permissionOptions = getAvailablePermissions();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={"xs"}
            fullWidth
            fullScreen={fullScreen}
            transitionDuration={500}
            PaperProps={{
                sx: {
                    borderTopLeftRadius: 10,
                    borderTopRightRadius: 10,
                    borderBottomLeftRadius: !fullScreen && 10,
                    borderBottomRightRadius: !fullScreen && 10,
                    p: 2,
                    width: "100%",
                    mx: "auto",
                    mt: fullScreen ? "auto" : undefined,
                    maxHeight: "80vh", // Ensures it doesn't overflow
                    overflow: "hidden", // Keeps scroll within dialog
                    display: "flex",
                    flexDirection: "column"
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
                <DialogTitle sx={{ fontWeight: "bold", fontSize: 16, p: 0 }}>
                    permissions
                </DialogTitle>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Box>
            {systemRole && systemRole.roleName !== 'default' ? (
                <>
                    <DialogContent sx={{ px: 0, mt: 2 }}>
                        <FormGroup>
                            {permissionOptions?.map((option) => {                                
                                // Disable manageTeamMembers and manageWallet if not owner
                                const isOwnerDisabled = !isOwnBusiness && (option.value === "manageTeamMembers" || option.value === "manageWallet");
                                
                                // Disable all other permissions when "no permissions" is selected
                                const isViewNothingSelected = permissions?.viewNothing === true;
                                const isDisabledByViewNothing = isViewNothingSelected && option.value !== "viewNothing";
                                
                                const isDisabled = isOwnerDisabled || isDisabledByViewNothing;

                                return (

                                    <FormControlLabel
                                        key={option.value}
                                        control={
                                            <Checkbox
                                                checked={permissions?.[option.value] === true}
                                                onChange={() => handleChange(option.value)}
                                                disabled={isDisabled}
                                                sx={{ 
                                                    color: "#6C00E6", 
                                                    "&.Mui-checked": { color: "#6C00E6" },
                                                    "&.Mui-disabled": { color: "#ccc" }
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography component="div">
                                                <Box component="span" sx={{ color: isDisabled ? "#999" : "#6C00E6" }}>
                                                    {option.label}
                                                </Box>
                                                {option.subtitle && (
                                                    <Box
                                                        component="span"
                                                        sx={{ color: "#7d7d7d", fontSize: "0.8rem", ml: 0.5 }}
                                                    >
                                                        {option.subtitle}
                                                    </Box>
                                                )}
                                            </Typography>
                                        }
                                        sx={{ alignItems: "center", mb: 1, opacity: isDisabled ? 0.6 : 1 }}
                                    />
                                )
                            })}
                        </FormGroup>
                    </DialogContent>
                    <Box sx={{ textAlign: "center", mt: 2 }}>
                        <Button
                            variant="contained"
                            onClick={onClose}
                            sx={{
                                bgcolor: "#6C00E6",
                                px: 5,
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: 15,
                                "&:hover": {
                                    bgcolor: "#5a00c7",
                                },
                            }}
                        >
                            save
                        </Button>
                    </Box>
                </>
            ) : (
                <DialogContent sx={{ px: 0, mt: 2 }}>
                    <Typography variant="h5" fontWeight={600} sx={{ textAlign: 'center', color: '#666' }}>
                        {!systemRole ? 'Please select a system role first' : 'Default role has no permissions to configure'}
                    </Typography>
                </DialogContent>
            )
            }
        </Dialog>
    );
};

export default PermissionsDialog;
